/**
 * Weekly 1:1 KPI scorecard — mirrors the Airtable Account Manager weekly review.
 * Overall score = average of per-KPI attainment (Close Rate excluded while target is N/A).
 */
import { inclusiveDayCount } from './dates.js';

export const WEEKLY_KPI_TARGETS = {
  totalDials: 200,
  liveConnectsMin: 90,
  liveConnectsMax: 150,
  qualifiedConversationsPerDay: 20,
  newSupportersAdded: 2,
  devicesReceived: 250,
  avgDevicesPerSupporter: 20,
  crmCompliance: 100,
} as const;

export type WeeklyKpiInput = {
  periodStart: string;
  periodEnd: string;
  totalDials: number;
  liveConnects: number;
  qualifiedConversations: number;
  newSupportersAdded: number;
  devicesReceived: number;
  closeRate: number | null;
  avgDevicesPerSupporter: number;
  crmCompliance: number;
  wins?: string;
  blockers?: string;
  actions?: string;
};

export type WeeklyScorecard = WeeklyKpiInput & {
  kind: 'weekly';
  overallPerformanceScore: number;
  wins: string;
  blockers: string;
  actions: string;
};

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

/** Live Connects: below min = proportional; inside/above band = 100. */
export function scoreLiveConnects(actual: number): number {
  if (actual >= WEEKLY_KPI_TARGETS.liveConnectsMin) return 100;
  return clampPct((actual / WEEKLY_KPI_TARGETS.liveConnectsMin) * 100);
}

export function scoreAgainstTarget(actual: number, target: number): number {
  if (target <= 0) return 0;
  return clampPct((actual / target) * 100);
}

export function computeWeeklyOverallScore(input: WeeklyKpiInput): number {
  const days = Math.max(1, inclusiveDayCount(input.periodStart, input.periodEnd));
  const qualifiedTarget = WEEKLY_KPI_TARGETS.qualifiedConversationsPerDay * days;

  const parts = [
    scoreAgainstTarget(input.totalDials, WEEKLY_KPI_TARGETS.totalDials),
    scoreLiveConnects(input.liveConnects),
    scoreAgainstTarget(input.qualifiedConversations, qualifiedTarget),
    scoreAgainstTarget(input.newSupportersAdded, WEEKLY_KPI_TARGETS.newSupportersAdded),
    scoreAgainstTarget(input.devicesReceived, WEEKLY_KPI_TARGETS.devicesReceived),
    scoreAgainstTarget(input.avgDevicesPerSupporter, WEEKLY_KPI_TARGETS.avgDevicesPerSupporter),
    clampPct(input.crmCompliance),
  ];

  const avg = parts.reduce((a, b) => a + b, 0) / parts.length;
  return Math.round(avg * 10) / 10;
}

export function buildWeeklyScorecard(input: WeeklyKpiInput): WeeklyScorecard {
  return {
    kind: 'weekly',
    periodStart: input.periodStart.slice(0, 10),
    periodEnd: input.periodEnd.slice(0, 10),
    totalDials: input.totalDials,
    liveConnects: input.liveConnects,
    qualifiedConversations: input.qualifiedConversations,
    newSupportersAdded: input.newSupportersAdded,
    devicesReceived: input.devicesReceived,
    closeRate: input.closeRate,
    avgDevicesPerSupporter: input.avgDevicesPerSupporter,
    crmCompliance: input.crmCompliance,
    overallPerformanceScore: computeWeeklyOverallScore(input),
    wins: input.wins ?? '',
    blockers: input.blockers ?? '',
    actions: input.actions ?? '',
  };
}

export function isWeeklyScorecard(ratings: unknown): ratings is WeeklyScorecard {
  return !!ratings && typeof ratings === 'object' && (ratings as { kind?: string }).kind === 'weekly';
}
